import React from "react";
import { useTranslation } from "react-i18next";
import { SessionContext } from "../../types/sessionContext";
import { FormField } from "../../components/FormField";
import { getCurrentDemoConfig } from "../../config/demoSites";

interface Step3FormProps {
    currentStep: number;
    sessionContext: SessionContext;
    updateSessionContext: (newCtx: SessionContext) => void;
}

const Step3Form: React.FC<Step3FormProps> = ({ currentStep, sessionContext, updateSessionContext }) => {
    const { t } = useTranslation();
    const fields = sessionContext.layout.workflow.steps[currentStep].fields;
    const demoConfig = getCurrentDemoConfig();
    const stepConfig = demoConfig.steps.find(step => step.id === currentStep);

    // Get all fields from all previous steps for summary display
    const allFields = {
        ...(sessionContext.layout.workflow.steps[1]?.fields || {}), // Step 1
        ...(sessionContext.layout.workflow.steps[2]?.fields || {}), // Step 2
        ...fields // Current step
    };

    const handleChange = (field: string, value: any) => {
        const updated = { ...sessionContext };
        updated.layout.workflow.steps[currentStep].fields[field] = value;
        updateSessionContext(updated);
    };

    // Generate confirmation number if applicable
    React.useEffect(() => {
        const confirmationField = stepConfig?.fields.find(f => f.fieldName === "confirmationNumber");
        if (confirmationField && !fields.confirmationNumber) {
            const random = Math.floor(100000 + Math.random() * 900000);
            const confirmation = `TRN-${random}`;
            handleChange("confirmationNumber", confirmation);
        }
    }, []);

    return (
        <div>
            <h1 className="text-2xl font-semibold">{t(stepConfig?.titleKey || "base_demo.workflow.steps.step3form.title")}</h1>
            <p className="mb-4 text-gray-500">{t("base_demo.step3form.description")}</p>

            {/* Summary section if this is a confirmation step */}
            {currentStep >= 3 && (
                <div className="mb-6 rounded border border-gray-200 bg-gray-50 p-4">
                    <h2 className="mb-3 text-lg font-medium">{t("base_demo.step3form.summary.title", "Summary")}</h2>

                    {/* Add summary sections based on your data model */}
                    {/* This is just an example - customize based on your application's needs */}
                    {allFields.vehicleDetails && (
                        <div className="mb-4">
                            <h3 className="mb-2 font-medium">{t("base_demo.step3form.summary.vehicle", "Vehicle Details")}</h3>
                            <p className="text-sm">{allFields.vehicleDetails || "-"}</p>
                        </div>
                    )}

                    {allFields.firstName && (
                        <div className="mb-4">
                            <h3 className="mb-2 font-medium">{t("base_demo.step3form.summary.personal", "Personal Information")}</h3>
                            <p className="text-sm">
                                {`${allFields.firstName || ""} ${allFields.lastName || ""}`}
                                {allFields.email && (
                                    <span>
                                        <br />
                                        {allFields.email}
                                    </span>
                                )}
                                {allFields.phoneNumber && (
                                    <span>
                                        <br />
                                        {allFields.phoneNumber}
                                    </span>
                                )}
                            </p>
                        </div>
                    )}

                    {/* Add more summary sections as needed */}
                </div>
            )}

            {/* Dynamically render fields based on configuration */}
            {stepConfig?.fields.map(fieldConfig => (
                <FormField
                    key={fieldConfig.fieldName}
                    field={fieldConfig}
                    value={fields[fieldConfig.fieldName] || ""}
                    onChange={value => handleChange(fieldConfig.fieldName, value)}
                    allFieldValues={fields}
                />
            ))}
        </div>
    );
};

export default Step3Form;
